import React, { useMemo, useRef } from 'react'
import { useMutation } from '@apollo/react-hooks'
import { ClientOptionsProvider as PSClientOptionsProvider } from '@paintscout/ui-business'
import { gql } from 'apollo-boost'
import type { OptionsDocument, RatesDocument } from 'paintscout'
import { useAppDocsQuery, AppDocsDocument } from '@paintscout/api'
import { sanitizeOptions } from '@paintscout/util/builder'
import cloneDeep from 'lodash/cloneDeep'
import set from 'lodash/set'

interface SaveOptionsResponse {
  options: {
    __typename: 'JSON'
  } & OptionsDocument
  rates: {
    __typename: 'JSON'
  } & RatesDocument
}

export default function ClientOptionsProvider({
  children,
  clientId
}: {
  children: React.ReactNode
  clientId?: string
}) {
  const { data: appDocs } = useAppDocsQuery({ fetchPolicy: 'cache-and-network' })
  const appDocsData = appDocs?.appDocs
  const fallbackOptions = useMemo(
    () =>
      ({
        options: {
          primaryColor: {
            value: '#0766EE'
          },
          secondaryColor: {
            value: '#FFFFFF'
          },
          features: {
            fontScaling: {
              enabled: false
            }
          },
          discounts: {
            order: [],
            values: {}
          },
          labels: {
            quote: 'Quote',
            workOrder: 'Work Order',
            application: 'Coat',
            labour: 'Painting',
            prep: 'Prep'
          },
          taxRates: [],
          emails: {
            followUps: {
              order: []
            },
            customTemplates: {
              order: []
            }
          }
        }
      }) as OptionsDocument,
    []
  )
  const fallbackRates = useMemo(() => ({}) as RatesDocument, [])
  const optionsData = (appDocsData?.options as OptionsDocument | undefined) ?? fallbackOptions
  const ratesData = (appDocsData?.rates as RatesDocument | undefined) ?? fallbackRates

  const [saveOptions] = useMutation<SaveOptionsResponse>(
    gql`
      mutation saveOptions($options: JSON, $rates: JSON, $saveRates: Boolean!, $saveOptions: Boolean!) {
        options: saveOptions(input: $options) @include(if: $saveOptions)
        rates: saveRates(input: $rates) @include(if: $saveRates)
      }
    `,
    {
      update: (cache, { data }) => {
        if (!appDocsData) return

        cache.writeQuery({
          query: AppDocsDocument,
          data: {
            ...appDocs,
            appDocs: {
              ...appDocsData,
              options: data.options || appDocsData.options,
              rates: data.rates || appDocsData.rates
            }
          }
        })
      }
    }
  )

  const [savePartialOptions] = useMutation<{ output: string }>(
    gql`
      mutation ($input: String!, $key: String!) {
        output: savePartialOptions(input: $input, key: $key)
      }
    `,
    {
      update: (cache, { data }) => {
        if (!data?.output) return

        // Only returns the updated field of options
        const { value: updatedField, key } = JSON.parse(data.output)
        const updatedAppDocs = cloneDeep(appDocs)
        set(updatedAppDocs, `appDocs.options.${key}`, updatedField)
        cache.writeQuery({
          query: AppDocsDocument,
          data: updatedAppDocs
        })
      }
    }
  )

  const [syncOptions] = useMutation(
    gql`
      mutation syncOptions($optionsRev: String!, $ratesRev: String!) {
        options: syncOptions(rev: $optionsRev)
        rates: syncRates(rev: $ratesRev)
      }
    `,
    {
      update: (cache, { data }) => {
        if (!appDocsData) return

        cache.writeQuery({
          query: AppDocsDocument,
          data: {
            ...appDocs,
            appDocs: {
              ...appDocsData,
              options: data.options || appDocsData.options,
              rates: data.rates || appDocsData.rates
            }
          }
        })
      }
    }
  )

  const lastSanitizedOptions = useRef<ReturnType<typeof sanitizeOptions>>(null)
  const lastRates = useRef<RatesDocument>(null)
  const sanitizedOptions = useMemo(() => {
    if (!appDocs?.appDocs?.options) return lastSanitizedOptions.current
    lastSanitizedOptions.current = sanitizeOptions(appDocs.appDocs.options)
    return lastSanitizedOptions.current
  }, [appDocs])
  const rates = appDocs?.appDocs?.rates ?? lastRates.current
  if (appDocs?.appDocs?.rates) lastRates.current = appDocs.appDocs.rates

  return (
    <PSClientOptionsProvider
      options={sanitizedOptions}
      rates={rates}
      clientId={clientId}
      onSync={({ optionsRev, ratesRev }) => {
        return syncOptions({
          variables: {
            optionsRev,
            ratesRev
          },
          optimisticResponse: {
            options: {
              ...optionsData,
              __typename: 'JSON' as const
            },
            rates: {
              ...ratesData,
              __typename: 'JSON' as const
            }
          }
        }).then((res) => ({
          options: res.data?.options ?? optionsData,
          rates: res.data?.rates ?? ratesData
        }))
      }}
      onSave={({ options, rates }) => {
        return saveOptions({
          variables: {
            options,
            rates,
            saveOptions: !!options,
            saveRates: !!rates
          },
          optimisticResponse: {
            options: options
              ? {
                  ...options,
                  __typename: 'JSON' as const
                }
              : undefined,
            rates: rates
              ? {
                  ...rates,
                  __typename: 'JSON' as const
                }
              : undefined
          }
        }).then((res) => ({
          options: res.data?.options ?? options,
          rates: res.data?.rates ?? rates
        }))
      }}
      onPartialOptionsSave={({ input, key }) => {
        return savePartialOptions({
          variables: {
            input,
            key
          }
        }).then((res) => ({
          output: res.data?.output
        }))
      }}
    >
      {children}
    </PSClientOptionsProvider>
  )
}
